// =====================================================================
// AuthContext.jsx - context provider pentru auth state (Faza 3.4b)
// =====================================================================
// Refactor din useAuth.jsx (Faza 3.4a era hook standalone -> instante
// independente per component -> L-047 race condition la login).
//
// API public NESCHIMBAT (backwards compat 100%):
//   const { user, permissions, loading, login, logout, can } = useAuth();
//
// Architecture:
//   - <AuthProvider> wrap-eaza tot App-ul (in main.jsx, intre BrowserRouter
//     si App). Stocheaza state-ul intr-o singura instanta useState.
//   - useAuth() consuma contextul cu useContext.
//   - Toate componentele care apeleaza useAuth() primesc EXACT acelasi
//     obiect referinta -> orice update (setUser, setPermissions) declanseaza
//     re-render in toate consumatori, ZERO stale state.
//
// Beneficii:
//   - L-047 fix nativ: dupa login, App.jsx isi vede user actualizat
//     instant -> route guard accept fara reload.
//   - Performance: auth.me() apelat O SINGURA DATA la mount, NU la fiecare
//     schimbare de ruta cum era inainte (cand fiecare pagina avea hook-ul
//     ei separate cu useEffect propriu).
//
// Reset Login.jsx workaround: window.location.href -> navigate() din nou.
// =====================================================================

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth } from '../services/api.js';

const AuthContext = createContext(null);

/**
 * Provider component. Wrap-eaza tot App-ul (in main.jsx).
 *
 * Singura instanta de state pentru intreaga aplicatie. Toti consumatorii
 * useAuth() impart aceeasi referinta.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Fetch initial: o singura data la mount Provider (= mount aplicatie).
  // Fara dependinte -> ZERO re-fetch la schimbari de ruta.
  useEffect(() => {
    auth.me()
      .then(data => {
        setUser(data.user);
        setPermissions(new Set(data.permissions || []));
      })
      .catch(() => {
        setUser(null);
        setPermissions(new Set());
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * Login: trimite credentials la backend, asteapta cookie set,
   * apoi reread /me pentru permisiuni proaspete.
   * Update-urile setUser/setPermissions propaga IN TOATE consumatori
   * useAuth() prin context -> App.jsx route guard vede user instant.
   */
  const login = async (username, password) => {
    const res = await auth.login(username, password);
    setUser(res.user);
    // Re-fetch /me pentru permisiuni (login response are user, NU permisiuni)
    const me = await auth.me();
    setPermissions(new Set(me.permissions || []));
    return res.user;
  };

  /**
   * Logout: cleanup state + redirect hard la /login.
   * Hard redirect (window.location.href) folosit aici intentionat:
   * vrem session cleanup complet, NU SPA navigate.
   */
  const logout = async () => {
    try { await auth.logout(); } catch (_e) { /* ignore */ }
    setUser(null);
    setPermissions(new Set());
    window.location.href = '/login';
  };

  /**
   * Permission check helper.
   * Accepta string (1 perm) sau array (oricare din ele = match).
   */
  const can = useCallback((permission) => {
    if (Array.isArray(permission)) {
      return permission.some(p => permissions.has(p));
    }
    return permissions.has(permission);
  }, [permissions]);

  // Day 9d — refresh user from server (e.g. after password change clears mustChangePassword flag)
  const refreshUser = async () => {
    try {
      const data = await auth.me();
      setUser(data.user);
      setPermissions(new Set(data.permissions || []));
      return data.user;
    } catch (e) {
      return null;
    }
  };

  const value = { user, permissions, loading, login, logout, can, refreshUser };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook consumer. ARUNCA eroare daca e folosit in afara <AuthProvider>
 * -> evita bug-uri silentioase de tip "user e mereu null" daca cineva
 * uita sa wrappe Provider-ul.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error(
      'useAuth() must be used within an <AuthProvider>. ' +
      'Wrap your <App /> with <AuthProvider> in main.jsx.'
    );
  }
  return ctx;
}
